import React, { type ReactElement, type ReactNode } from "react";
import { Alert, AlertDescription, Button, Checkbox, Label, cn } from "@ndd/ui";
import type {
  ClientNotificationSetting,
  ClientNotificationSettingsResponse,
} from "../../../../shared/api-types";
import { THEMED_CARD, THEMED_DIVIDE } from "../../lib/always-look";
import {
  messageTypeLabel,
  reminderTimeLabel,
} from "../../lib/notification-settings-client";
import {
  USE_DEFAULT,
  cardRows,
  defaultValueUnknown,
  draftChanges,
  draftProblem,
  draftFollowsDefault,
  draftSourceLabel,
  messageTypeDescription,
  rowKey,
  rowSourceLabel,
  shownRow,
  timingMenu,
  timingSummary,
  toggledEnabled,
  withReminderTime,
  type CardNotice,
  type DraftRow,
  type NotificationsDraft,
} from "../../lib/patient-notifications-client";
import { CARD_HEADING } from "./patient-page-parts";

// The patient page's Notifications card (NO-851): one Client's appointment
// emails, each with where its value comes from, and an editor that saves the
// Client's own choices. A leaf module (`import React`, relative imports, no
// router, no queries) so `tsx --test` renders it; the page supplies the read,
// the draft and the save. Source labels are neutral text, never colored.

type View = ClientNotificationSettingsResponse;
type Row = ClientNotificationSetting;

export const NOTIFICATIONS_INTRO = "Emails about this Client's appointments.";

export const NOTIFICATIONS_UNAVAILABLE_MESSAGE =
  "Notification choices aren't available right now.";

export const CONFLICT_MESSAGE =
  "Someone else changed these choices since you opened them, so nothing was saved. The card now shows the latest choices. Check them and edit again if you still need to.";

export const TOMBSTONED_MESSAGE =
  "These choices can't be changed for this account. Ask an admin if this looks wrong.";

export const SAVE_FAILED_MESSAGE = "These choices weren't saved. Try again.";

export function changedByOthersMessage(messageTypes: readonly string[]): string {
  return `Saved. Someone else also changed ${messageTypes.join(", ")} since you opened these choices. The card now shows the latest choices.`;
}

export type NotificationsCardState =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "ready"; view: View };

export interface PatientNotificationsCardProps {
  state: NotificationsCardState;
  /** The open editor's draft; null while the card reads. */
  draft: NotificationsDraft | null;
  notice: CardNotice | null;
  saving: boolean;
  onRetry: () => void;
  onEdit: () => void;
  onDraft: (key: string, row: DraftRow) => void;
  onSave: () => void;
  onCancel: () => void;
}

const QUIET_BUTTON = "h-auto px-0 py-0 text-body";

/**
 * @ndd/ui's Checkbox draws its check mark for the indeterminate state too, so
 * an unknown default would look on. Here it shows a dash instead.
 */
const INDETERMINATE_MARK =
  "data-[state=indeterminate]:[&_svg]:hidden data-[state=indeterminate]:after:leading-none data-[state=indeterminate]:after:content-['−']";

/** Edit's accessible name, apart from About them's Edit on the same page. */
export const EDIT_BUTTON_NAME = "Edit notification choices";

/** While loading the card is its heading alone. */
export function PatientNotificationsCard(
  props: PatientNotificationsCardProps
): ReactElement {
  return (
    <section
      aria-label="Notifications"
      className={cn("rounded-xl border border-border bg-card px-7 py-6", THEMED_CARD)}
    >
      <h2 className={CARD_HEADING}>Notifications</h2>
      <CardBody {...props} />
    </section>
  );
}

function CardBody(props: PatientNotificationsCardProps): ReactElement | null {
  const { state, draft } = props;

  if (state.kind === "loading") return null;

  if (state.kind === "failed") return <UnavailableLine onRetry={props.onRetry} />;

  return (
    <>
      <p className="mt-1 text-body-sm text-muted-foreground">{NOTIFICATIONS_INTRO}</p>
      {draft === null ? (
        <ReadView view={state.view} notice={props.notice} onEdit={props.onEdit} />
      ) : (
        <EditView {...props} view={state.view} draft={draft} />
      )}
    </>
  );
}

function UnavailableLine({ onRetry }: { onRetry: () => void }): ReactElement {
  return (
    <div className="mt-4">
      <p className="text-body text-muted-foreground">
        {NOTIFICATIONS_UNAVAILABLE_MESSAGE}
      </p>
      <Button
        type="button"
        variant="quiet-link"
        className={cn("mt-2", QUIET_BUTTON)}
        onClick={onRetry}
      >
        Retry
      </Button>
    </div>
  );
}

function ReadView({
  view,
  notice,
  onEdit,
}: {
  view: View;
  notice: CardNotice | null;
  onEdit: () => void;
}): ReactElement {
  return (
    <>
      <RowList>
        {cardRows(view).map((row) => (
          <ReadRow key={rowKey(row)} row={row} />
        ))}
      </RowList>
      <Notice notice={notice} />
      <Button
        type="button"
        variant="quiet-link"
        className={cn("mt-4", QUIET_BUTTON)}
        aria-label={EDIT_BUTTON_NAME}
        onClick={onEdit}
      >
        Edit
      </Button>
    </>
  );
}

function RowList({ children }: { children: ReactNode }): ReactElement {
  return (
    <ul className={cn("mt-4 divide-y divide-border", THEMED_DIVIDE)}>{children}</ul>
  );
}

function ReadRow({ row }: { row: Row }): ReactElement {
  const timingSource =
    row.timingFrom === row.enabledFrom ? null : rowSourceLabel(row, row.timingFrom);

  return (
    <li data-message-type={row.kind} className="py-3 first:pt-0 last:pb-0">
      <p className="text-body font-semibold text-foreground">
        {messageTypeLabel(row.kind)}
      </p>
      <ValueLine
        value={row.enabled ? "Email on" : "Email off"}
        source={rowSourceLabel(row, row.enabledFrom)}
      />
      {row.timed && row.enabled ? (
        <ValueLine value={timingSummary(row.offsetsMinutes)} source={timingSource} />
      ) : null}
    </li>
  );
}

function ValueLine({
  value,
  source,
}: {
  value: string;
  source: string | null;
}): ReactElement {
  return (
    <p className="mt-1 text-body-sm text-foreground">
      {value}
      {source ? (
        <>
          <span aria-hidden="true" className="text-subtle">
            {" · "}
          </span>
          {/* The dot is hidden from assistive tech, which hears a comma instead. */}
          <span className="sr-only">, </span>
          <span data-source="" className="text-muted-foreground">
            {source}
          </span>
        </>
      ) : null}
    </p>
  );
}

function Notice({ notice }: { notice: CardNotice | null }): ReactElement | null {
  if (notice === null) return null;

  if (notice.kind === "saved") {
    return (
      <p
        role="status"
        data-save-notice="saved"
        className="mt-4 text-body-sm text-muted-foreground"
      >
        Saved.
      </p>
    );
  }

  const { variant, message } = noticeAlert(notice);

  return (
    <Alert variant={variant} className="mt-4" data-save-notice={notice.kind}>
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

/**
 * A conflict is amber: the card already shows the right values. A tombstone is
 * neutral: retrying cannot fix it. Only a failed save, which retrying can, is red.
 */
function noticeAlert(notice: Exclude<CardNotice, { kind: "saved" }>): {
  variant: "info" | "warning" | "default" | "danger";
  message: string;
} {
  switch (notice.kind) {
    case "changed":
      return { variant: "info", message: changedByOthersMessage(notice.messageTypes) };
    case "conflict":
      return { variant: "warning", message: CONFLICT_MESSAGE };
    case "tombstoned":
      return { variant: "default", message: TOMBSTONED_MESSAGE };
    case "failed":
      return { variant: "danger", message: SAVE_FAILED_MESSAGE };
  }
}

type EditViewProps = PatientNotificationsCardProps & {
  view: View;
  draft: NotificationsDraft;
};

function EditView(props: EditViewProps): ReactElement {
  const { view, draft, saving, onDraft } = props;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        props.onSave();
      }}
    >
      <RowList>
        {cardRows(view).map((row) => {
          const key = rowKey(row);
          const drafted = draft[key];
          if (!drafted) return null;

          return (
            <EditRow
              key={key}
              row={row}
              draft={drafted}
              timingMinutes={timingMenu(view)}
              disabled={saving}
              onDraft={(next) => onDraft(key, next)}
            />
          );
        })}
      </RowList>
      <EditFooter {...props} />
    </form>
  );
}

interface EditRowProps {
  row: Row;
  draft: DraftRow;
  timingMinutes: readonly number[];
  disabled: boolean;
  onDraft: (next: DraftRow) => void;
}

function EditRow({
  row,
  draft,
  timingMinutes,
  disabled,
  onDraft,
}: EditRowProps): ReactElement {
  const id = `patient-notifications-${row.kind}`;
  const shown = shownRow(row, draft);
  const unknown = defaultValueUnknown(row, draft);

  return (
    <li data-message-type={row.kind} className="py-4 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3">
        <Checkbox
          id={id}
          className={cn("mt-0.5", INDETERMINATE_MARK)}
          checked={unknown ? "indeterminate" : shown.enabled}
          disabled={disabled}
          onCheckedChange={(checked) =>
            onDraft(toggledEnabled(row, draft, checked === true))
          }
        />
        <div className="min-w-0">
          <Label htmlFor={id} className="text-body font-semibold text-foreground">
            {messageTypeLabel(row.kind)}
          </Label>
          <p className="text-body-sm text-muted-foreground">
            {messageTypeDescription(row.kind)}
          </p>
          <DraftSource row={row} draft={draft} disabled={disabled} onDraft={onDraft} />
        </div>
      </div>
      {row.timed && !unknown ? (
        <ReminderTimes
          id={id}
          draft={draft}
          shown={shown.offsetsMinutes}
          timingMinutes={timingMinutes}
          disabled={disabled || !shown.enabled}
          onDraft={onDraft}
        />
      ) : null}
    </li>
  );
}

function DraftSource({
  row,
  draft,
  disabled,
  onDraft,
}: Omit<EditRowProps, "timingMinutes">): ReactElement {
  return (
    <p className="mt-1 flex flex-wrap items-center gap-x-2 text-meta text-muted-foreground">
      <span data-source="">{draftSourceLabel(row, draft)}</span>
      {draftFollowsDefault(row, draft) ? null : (
        <Button
          type="button"
          variant="quiet-link"
          size="sm"
          className="h-auto p-0 text-meta"
          disabled={disabled}
          aria-label={`Use the default for ${messageTypeLabel(row.kind)}`}
          onClick={() => onDraft(USE_DEFAULT)}
        >
          Use the default
        </Button>
      )}
    </p>
  );
}

function ReminderTimes({
  id,
  draft,
  shown,
  timingMinutes,
  disabled,
  onDraft,
}: {
  id: string;
  draft: DraftRow;
  shown: number[];
  timingMinutes: readonly number[];
  disabled: boolean;
  onDraft: (next: DraftRow) => void;
}): ReactElement {
  return (
    <fieldset className="mt-3 pl-7" disabled={disabled}>
      <legend className="text-body-sm font-semibold text-foreground">Send it</legend>
      <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2">
        {timingMinutes.map((minutes) => {
          const timeId = `${id}-time-${minutes}`;

          return (
            <div key={minutes} className="flex items-center gap-2">
              <Checkbox
                id={timeId}
                checked={shown.includes(minutes)}
                disabled={disabled}
                onCheckedChange={(checked) =>
                  onDraft(withReminderTime(draft, shown, minutes, checked === true))
                }
              />
              <Label htmlFor={timeId} className="text-body-sm font-normal">
                {reminderTimeLabel(minutes)}
              </Label>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

function EditFooter({
  view,
  draft,
  notice,
  saving,
  onCancel,
}: EditViewProps): ReactElement {
  const problem = draftProblem(view, draft);
  const unchanged = draftChanges(view, draft).length === 0;

  return (
    <>
      {problem ? (
        <p role="alert" className="mt-4 text-body-sm text-danger">
          {problem}
        </p>
      ) : null}
      <Notice notice={notice} />
      <div className="mt-5 flex items-center gap-4">
        <Button
          type="submit"
          variant="press"
          disabled={saving || unchanged || problem !== null}
        >
          Save
        </Button>
        <Button
          type="button"
          variant="quiet-link"
          className={QUIET_BUTTON}
          disabled={saving}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </>
  );
}
